// #1138: api.ts をドメイン別ファイルに機械分割した façade。
// 呼び出し側は従来どおり `import { api, ... } from '../services/api'` のまま
// (ディレクトリの index.ts に解決される)。メソッドの実体は各ドメインファイル参照。
//
// ============================================================
// 新しい API メソッドを足すときの配置ルール
// ============================================================
// 「誰の操作か (顧客 / 店舗オーナー / YOKOSO運営)」→「何のドメインか」の順で決める:
//
//   _client.ts      fetch ラッパ / ApiError / 認証トークン処理。ドメインロジック禁止。
//                   api/ ディレクトリ内部専用 (index.ts から re-export しない)
//   auth.ts         顧客のアカウント: ログイン/セッション/プロフィール/退会/
//                   ウォレット/カード/お気に入り/チャージ
//   devices.ts      端末・認証器のセキュリティ: WebAuthn(Passkey)/端末承認/
//                   SMS復旧/TOTP (顧客・店舗どちらも)
//   stores.ts       顧客から見た店舗: 店舗閲覧/卓QR/店舗セッション/レビュー/メニュー閲覧
//   orders.ts       注文と決済: 決済実行(mock/Stripe)/self-order/卓会計/現金/
//                   伝票QR/注文チャット
//   reservations.ts 予約とコース (予約CRUD/変更/公開share/slots/コースCRUD)
//   storeOwner.ts   店舗オーナー・スタッフの運営操作: store-auth/スタッフ管理/返金/
//                   Stripe Connect/dispute evidence/geocode/メニューOCR
//   admin.ts        YOKOSO 運営(プラットフォーム管理者)専用: budgets/alerts/
//                   退会監査/owner usage
//   billing.ts      プラットフォーム → 店舗オーナーへの請求: 今月のご請求/
//                   店舗別内訳/支払い履歴/超過分の支払い
//   stamp.ts        スタンプラリー (イベント系機能はここに追随)
//   plan.ts         旅前プランナー (#1237): プラン生成/スポットカタログ/保存プラン
//   domains/experience/api.ts  体験チケット (#1248): 商品一覧/購入/予約一覧/
//                   QR/店舗スタッフ消し込み
//   domains/lodging/api.ts     宿泊代行リクエスト (#1665): 申込/自分の一覧/取り下げ
//   retail.ts       物販 (お土産) の店頭 POS / 在庫: 在庫調整/物販会計 (#1182)
//
// 迷ったら: ①既に似た API がいるファイルに置く ②上記の「誰の操作か」で分ける
// ③どこにも収まらない新ドメインは新ファイルを作り下の合成に1行足す
// (雑多な misc は作らない)。
// 型はそのメソッドを定義するファイルに置き、api/ の外から使う型だけ
// index.ts で re-export する。1ファイル 700 行超で分割を検討 (#1133 の趣旨)。
// メソッド名は全ファイルを通して一意であること (スプレッド合成で後勝ちに
// なるため。CI の typecheck では検出されない点に注意)。
import { adminApi } from './admin'
import { billingApi } from './billing'
import { contactApi } from './contact'
import { authApi } from './auth'
import { devicesApi } from './devices'
import { ordersApi } from './orders'
import { planApi } from '../../domains/plan/api'
import { experienceApi } from '../../domains/experience/api'
import { lodgingApi } from '../../domains/lodging/api'
import { reservationsApi } from './reservations'
import { retailApi } from './retail'
import { announcementsApi } from './announcements'
import { stampApi } from './stamp'
import { storeOwnerApi } from './storeOwner'
import { storesApi } from './stores'

export { ApiError, getFirebaseIdToken, setStaffSessionToken } from './_client'
export type { StoreRecoEntry, StoreRecoReason, TableStayState, TableSessionRow } from './stores'
export type { RefundAlternateMethod, RefundBankAccount, RefundOnline, RefundOnlineKind, RefundRedeemMethod, RefundVoucher, StoreRefundVoucher } from './reservations'  // #2226 / #2308
export { CONTACT_CATEGORIES, CONTACT_MAX_FILES, CONTACT_MAX_FILE_BYTES } from './contact'
export type { ContactCategory, ContactInput } from './contact'
export type { BillingCharge, BillingPerStore, BillingStatement, BillingSummary, ChargebackGuarantee, StatementPerStore } from './billing'
export type { SaasNoticeStage, SaasOffer, SaasSubscriptionState } from './billing'
export type {
  PendingDeviceConfirmation,
  WebAuthnConfig,
  WebAuthnCredentialItem,
} from './devices'
export type {
  StampArCompleteResult,
  StampArEventBlock,
  StampArLaunchResult,
  StampArSpot,
  StampArSpotsResult,
  StampCollectedSpot,
  StampBooth,
  StampBoothLink,
  StampContext,
  StampEventInfo,
  StampEvent,
  StampPrizeSummary,
  StampLotterySummary,
  StampMyDraw,
  StampProgress,
  StampRegion,
  StampScanResult,
  StampSlot,
  StampSlotPlacement,
  StampLotteryKeywordResult,
  StampTicket,
  StampUpcomingBlock,
  StampActivityProgramBlock,
  StampEventBlock,
  StampEventGroup,
  StampEventInfoProgram,
  StampProgramBlock,
  StampQrProgramBlock,
  StampUpcomingEventBlock,
} from './stamp'
export type {
  GeneratedPlan,
  PlanArea,
  PlanAreaEvent,
  PlanAreaOptions,
  PlanCustomKind,
  PlanCustomSpot,
  PlanDay,
  PlanGenerateResult,
  PlanOptions,
  PlanRecommendationItem,
  PlanRecommendationResult,
  PlanSaveRequestBody,
  PlanSelectionLog,
  PlanShelf,
  PlanSlot,
  PlanSpot,
  SavedPlan,
  SavedTrip,
  SavedTripStop,
  TripGatewaySnapshot,
  TripSaveRequestBody,
  TripStopOverlayPatch,
  TripUpdateBody,
  TripWish,
  SavedPlanDay,
  SavedPlanSlot,
  SharedPlan,
  SharedPlanDay,
  SharedPlanSlot,
  AdminPlanSpot,
  AdminPlanSpotBody,
  AdminPlanTemplate,
  AdminPlanTemplateBody,
  AdminPlanTemplateSlot,
  PlanAttractReport,
  PlanAttractSpotRow,
  SharedTarget,
  SharedTrip,
  SharedTripStop,
  TripHotelNight,
} from '../../domains/plan/api'
export type {
  ExperienceProduct,
  ExperienceTicket,
  ExperienceSlot,
  TicketPaymentIntent,
  PublicExperienceShare,
  PublicExperienceTicket,
  OwnerExperienceProduct,
  ExperienceProductInput,
  ExperienceSchedule,
  ScheduleDay,
  StoreExperienceSlot,
  ExperienceSalesSummary,
  TransportTicket,
} from '../../domains/experience/api'
export type { AvailabilityResult, LodgingBrand, RoomOffer, StayRequest, StayRequestStatus } from '../../domains/lodging/api'
export type {
  DisputeEvidenceCategory,
  DisputeEvidenceFile,
  DisputeEvidenceResponse,
  EcVendorTerms,
  EcVendorTermsPublic,
  MerchantTerms,
  MerchantTermsState,
  OwnerSuspensionState,
  RefundOnlineMethods,
  StoreRegistrationPayload,
} from './storeOwner'

export const api = {
  ...devicesApi,
  ...authApi,
  ...storesApi,
  ...ordersApi,
  ...reservationsApi,
  ...storeOwnerApi,
  ...adminApi,
  ...billingApi,
  ...stampApi,
  ...planApi,
  ...experienceApi,
  ...lodgingApi,
  ...retailApi,
  ...announcementsApi,
  ...contactApi,
}
